import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { OrderMapOrderInfoPanel } from './OrderMapOrderInfoPanel';
import {
  buildOrderTimelineFromLogs,
  formatStatusLabel,
  fmtDate,
  fmtTime,
} from '../orderManagementOrderHelpers';

const TABS = [
  { id: 'info', label: 'Order info' },
  { id: 'tracking', label: 'Tracking' },
];

function timelineStepState({ isComplete, isCurrent }) {
  if (isCurrent) return 'active';
  if (isComplete) return 'done';
  return 'upcoming';
}

export function OrderTimeline({ order, onClose }) {
  const [activeTab, setActiveTab] = useState('tracking');

  useEffect(() => {
    setActiveTab('tracking');
  }, [order?.id]);

  const apiOrder = order?.raw ?? order;

  const timeline = useMemo(() => {
    if (!apiOrder) return [];
    return [...buildOrderTimelineFromLogs(apiOrder)].reverse();
  }, [apiOrder]);

  if (!order) return null;

  return (
    <div className='otd-timeline-card'>
      <div className='otd-timeline-card__header'>
        <div>
          <p className='otd-timeline-card__label'>Order</p>
          <h3 className='otd-timeline-card__title'>#{order.orderNo}</h3>
        </div>
        <div className='otd-timeline-card__header-actions'>
          <button type='button' className='otd-timeline-card__icon-btn' onClick={onClose} aria-label='Close'>
            <X size={14} />
          </button>
        </div>
      </div>

      <div className='otd-timeline-card__tabs' role='tablist' aria-label='Order details'>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type='button'
            role='tab'
            aria-selected={activeTab === tab.id}
            className={`otd-timeline-card__tab ${activeTab === tab.id ? 'otd-timeline-card__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'info' ? (
        <div className='otd-timeline-card__panel' role='tabpanel'>
          <OrderMapOrderInfoPanel order={apiOrder} />
        </div>
      ) : (
        <ul className='otd-timeline' role='tabpanel'>
          {timeline.map((entry, index) => {
            const state = timelineStepState(entry);
            const showTimestamp = Boolean(entry.timestamp) && (entry.isComplete || entry.isCurrent);
            return (
              <li
                key={`${entry.status}-${index}`}
                className={`otd-timeline__item otd-timeline__item--${state}`}
              >
                <div className='otd-timeline__track' aria-hidden>
                  <div className='otd-timeline__dot' />
                </div>
                <div className='otd-timeline__content'>
                  <p className='otd-timeline__title'>{formatStatusLabel(entry.status)}</p>
                  {showTimestamp && (
                    <p className='otd-timeline__time'>
                      {fmtDate(entry.timestamp)}
                      {fmtTime(entry.timestamp) ? ` · ${fmtTime(entry.timestamp)}` : ''}
                    </p>
                  )}
                  {entry.isCurrent && !showTimestamp && (
                    <p className='otd-timeline__time otd-timeline__time--current'>In progress</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
